import { Bundle, CollectionProduct, Transform, TransformCollection, WorkflowExperience } from '@spiffcommerce/core'
import { SpiffCommerce3DPreviewService } from '@spiffcommerce/preview'
import { getShopifyMetadata } from '../hooks/useShopifyMetadata'
import { client } from '../../helpers/spiff'

export const WORKFLOW_EXPERIENCE_OPTS = {
  assets: {
    metadata: true
  }
}

export async function createWorkflowExperiencesForPendants({
  baseWorkflowExperience,
  bundle,
  getProduct,
  quantity
}: {
  baseWorkflowExperience: WorkflowExperience
  bundle: Bundle
  getProduct: (index: number) => CollectionProduct | undefined
  quantity: number
}) {
  const { currentLength, transformCollection } = getTransformCollectionAndLength({
    bundle,
    baseWorkflowExperience: baseWorkflowExperience
  })
  if (!currentLength || !transformCollection) return

  const workflowExperienceData = Array.from({ length: quantity }).flatMap((_, index) => {
    const product = getProduct(index)
    if (!product) return []
    const position = index + 1

    const { transform, transformBody, workflowExperienceOpts } = getPendantWorkflows({
      product,
      position,
      quantity,
      transformCollection,
      currentLength
    })

    return [
      {
        transform,
        transformBody,
        workflowExperienceOpts
      }
    ]
  })

  const workflowExperiences = workflowExperienceData.map((data) => {
    return data.workflowExperienceOpts
  })

  const workflowExperiencesToAdd = await client.getWorkflowExperiences(workflowExperiences, WORKFLOW_EXPERIENCE_OPTS)
  return workflowExperiencesToAdd.flatMap((workflowExperience, i) => {
    const { transform, transformBody } = workflowExperienceData[i]
    if (!transform || !transformBody) return []
    return [
      {
        workflowExperience,
        transform: workflowExperienceData[i].transform,
        transformBody: workflowExperienceData[i].transformBody
      }
    ]
  }) as {
    workflowExperience: WorkflowExperience
    transform: Transform
    transformBody: Transform
  }[]
}

export async function updateWorkflowExperienceTransformsForPendants({
  workflowExperienceAndTransforms,
  isOnBodyPreview,
  bundle,
  previewService
}: {
  workflowExperienceAndTransforms: { workflowExperience: WorkflowExperience; transform: Transform; transformBody: Transform }[]
  isOnBodyPreview: boolean
  bundle: Bundle
  previewService: SpiffCommerce3DPreviewService
}) {
  const injectPendantsIntoPreviewService = workflowExperienceAndTransforms.map((workflowExperienceAndTransform) => {
    const pendantWorkflowExperience = workflowExperienceAndTransform.workflowExperience
    return pendantWorkflowExperience.getWorkflowManager().injectIntoPreviewService(previewService)
  })

  const updateWorkflowExperienceTransforms = workflowExperienceAndTransforms.map((workflowExperienceAndTransform) => {
    return Promise.all([
      bundle.updateWorkflowExperienceTransform(
        workflowExperienceAndTransform.workflowExperience,
        'default-position',
        workflowExperienceAndTransform.transform.get()
      ),
      bundle.updateWorkflowExperienceTransform(
        workflowExperienceAndTransform.workflowExperience,
        'body-position',
        workflowExperienceAndTransform.transformBody.get()
      )
    ])
  })

  const activateTransforms = workflowExperienceAndTransforms.map((workflowExperienceAndTransform) => {
    if (isOnBodyPreview) {
      return bundle.activateWorkflowExperienceTransform(workflowExperienceAndTransform.workflowExperience, 'body-position')
    } else {
      return bundle.activateWorkflowExperienceTransform(workflowExperienceAndTransform.workflowExperience, 'default-position')
    }
  })

  await Promise.all(injectPendantsIntoPreviewService)
  await Promise.all(updateWorkflowExperienceTransforms)
  await Promise.all(activateTransforms)
}

export function getPendantWorkflows({
  product,
  position,
  quantity,
  transformCollection,
  currentLength
}: {
  product: CollectionProduct
  position: number
  quantity: number
  transformCollection: TransformCollection
  currentLength: string
}) {
  const workflowExperienceOpts = prepareWorkflowExperience(product)
  const transform = transformCollection.getTransforms().find((t) => {
    if (t.getId() === `${currentLength}-${quantity}-${position}`) return t
  })

  const transformBody = transformCollection.getTransforms().find((t) => {
    if (t.getId() === `${currentLength}-${quantity}-${position}-b`) return t
  })

  return {
    workflowExperienceOpts,
    transform,
    transformBody
  }
}

export function getTransformCollectionAndLength({ bundle, baseWorkflowExperience }: { bundle: Bundle; baseWorkflowExperience: WorkflowExperience }) {
  const transformCollection = bundle.getProductCollection()?.getTransformCollection()
  const currentLength = baseWorkflowExperience
    .getSteps()
    .filter((step) => step.getTags().includes('length'))[0]
    ?.getCurrentVariant()
    ?.getName()
    ?.replace('"', '')

  return {
    transformCollection,
    currentLength
  }
}

function prepareWorkflowExperience(product: CollectionProduct) {
  const shopifyStoreUrl = getShopifyMetadata()?.shopifyStoreUrl
  const integrationProductId = product.getIntegrationByType('Shopify' as any, shopifyStoreUrl).id
  const workflowId = product.getDefaultWorkflow().getId()
  return {
    integrationProductId: integrationProductId,
    workflowId: workflowId,
    type: 'integration',
    workflowConfiguration: {
      singleVariantsRenderable: true
    }
  } as const
}
